// src/pages/HomePage.js
import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ScrollMenu } from "react-horizontal-scrolling-menu";
import "react-horizontal-scrolling-menu/dist/styles.css";
import {
  selectTransportMode,
  setStartPoint,
  setEndPoint,
  setAllTransportModes,
} from "../features/tripSlice";

import LocationInput from "../components/LocationInput";
import { useNavigate } from "react-router-dom";

const HomePage = () => {
  const dispatch = useDispatch();
  const { startPoint, endPoint, transportMode } = useSelector(
    (state) => state.trip
  );

  const [locationQuery, setLocationQuery] = useState({ start: "", end: "" });
  const [activeInput, setActiveInput] = useState("");

  const transportOptions = [
    {
      type: "Walk",
      group: "foot",
      icon: "/img/transportation/walk.png",
      co2e: 0.0,
      is_public: true,
    },
    {
      type: "Train",
      group: "car",
      icon: "/img/transportation/train.png",
      co2e: 0.04,
      is_public: true,
    },
    {
      type: "Bus",
      group: "car",
      icon: "/img/transportation/bus.png",
      co2e: 0.08,
      is_public: true,
    },
    {
      type: "Car",
      group: "car",
      icon: "/img/transportation/car.png",
      co2e: 0.27,
      is_public: false,
    },
    {
      type: "Motorcycle",
      group: "bike",
      icon: "/img/transportation/motorcycle.png", // Fixed lowercase for consistency
      co2e: 0.15,
      is_public: false,
    },
  ];

  const handleTransportSelect = (mode) => {
    const selectedTransport = transportOptions.find(
      (option) => option.type === mode
    );

    dispatch(
      selectTransportMode(mode === transportMode?.type ? "" : selectedTransport)
    );
  };

  const handleLocationChange = (query, type) => {
    setLocationQuery((prev) => ({ ...prev, [type]: query }));
  };

  const handleResultSelect = (result, type) => {
    const { name, lat, lon } = result;
    if (type === "start") {
      dispatch(setStartPoint({ name: name, lat, lon }));
    } else {
      dispatch(setEndPoint({ name: name, lat, lon }));
    }
    setLocationQuery((prev) => ({ ...prev, [type]: name }));
    setActiveInput(""); // Hide search results
  };

  const navigate = useNavigate();
  const handleCalculateClick = () => {
    dispatch(setAllTransportModes(transportOptions));
    navigate("/calculate"); // Pindah ke page "calculate"
  };

  const [isCollapseOpen, setIsCollapseOpen] = useState(false);

  const handleCollapseToggle = () => {
    setIsCollapseOpen(!isCollapseOpen);
  };

  const isButtonDisabled = !startPoint || !endPoint || !transportMode;

  return (
    <div className="container">
      <div className="content">
        <div className="logo">
          <img
            src="/logo.png"
            srcSet="/logo.png 1x, /logo@2x.png 2x"
            alt="Tracco Logo"
          />
          Tracco
        </div>

        <div className="header mb-d">
          <h1 className="title">Let’s Track Your Carbon Emission</h1>
          <p className="text">
            Enter your trip details and we'll help you calculate your commute
            emissions.
          </p>
        </div>

        <div className="point-input-container mb-d">
          <ul className="timeline">
            <LocationInput
              type="start"
              value={locationQuery.start}
              onChange={(query) => handleLocationChange(query, "start")}
              onFocus={() => setActiveInput("start")}
              onResultSelect={(result) => handleResultSelect(result, "start")}
            />

            {/* <div className="form-divider"></div> */}

            <LocationInput
              type="end"
              value={locationQuery.end}
              onChange={(query) => handleLocationChange(query, "end")}
              onFocus={() => setActiveInput("end")}
              onResultSelect={(result) => handleResultSelect(result, "end")}
            />
          </ul>
        </div>

        <section className="mb-24">
          <div className="header">
            <h1 className="title">Choose Transportation</h1>
            <p className="text">
              What rides you use to get to the destination?
            </p>
          </div>

          <ScrollMenu>
            {transportOptions.map((option, index) => (
              <div
                key={index}
                className={`transport-option ${
                  option.type === transportMode?.type ? "active" : ""
                }`}
                onClick={() => handleTransportSelect(option.type)}
              >
                <div className="icon-wrapper">
                  <img src={option.icon} alt={option.type} className="mb-2" />
                </div>
                <div className="content">
                  <div className="title">{option.type}</div>
                  <p className="info">
                    {option.co2e} CO<sub>2</sub>/km
                  </p>
                </div>
              </div>
            ))}
          </ScrollMenu>
        </section>

        <div className="info-container mb-5">
          <div className="icon-wrapper" onClick={handleCollapseToggle}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 1C5.925 1 1 5.925 1 12C1 18.075 5.925 23 12 23C18.075 23 23 18.075 23 12C23 5.925 18.075 1 12 1ZM11.5 6C11.2348 6 10.9804 6.10536 10.7929 6.29289C10.6054 6.48043 10.5 6.73478 10.5 7C10.5 7.26522 10.6054 7.51957 10.7929 7.70711C10.9804 7.89464 11.2348 8 11.5 8H12C12.2652 8 12.5196 7.89464 12.7071 7.70711C12.8946 7.51957 13 7.26522 13 7C13 6.73478 12.8946 6.48043 12.7071 6.29289C12.5196 6.10536 12.2652 6 12 6H11.5ZM10 10C9.73478 10 9.48043 10.1054 9.29289 10.2929C9.10536 10.4804 9 10.7348 9 11C9 11.2652 9.10536 11.5196 9.29289 11.7071C9.48043 11.8946 9.73478 12 10 12H11V15H10C9.73478 15 9.48043 15.1054 9.29289 15.2929C9.10536 15.4804 9 15.7348 9 16C9 16.2652 9.10536 16.5196 9.29289 16.7071C9.48043 16.8946 9.73478 17 10 17H14C14.2652 17 14.5196 16.8946 14.7071 16.7071C14.8946 16.5196 15 16.2652 15 16C15 15.7348 14.8946 15.4804 14.7071 15.2929C14.5196 15.1054 14.2652 15 14 15H13V11C13 10.7348 12.8946 10.4804 12.7071 10.2929C12.5196 10.1054 12.2652 10 12 10H10Z"
                fill="#A8B3BD"
              />
            </svg>
          </div>

          <div className="d-flex flex-column" style={{ gap: "8px" }}>
            <a
              href="#infoDetail"
              role="button"
              aria-expanded={isCollapseOpen}
              aria-controls="collapseExample"
              onClick={handleCollapseToggle}
            >
              How do we count the emission?
            </a>

            <div
              className={`collapse info-detail ${isCollapseOpen ? "show" : ""}`}
              id="infoDetail"
            >
              Tracco calculates emissions based on established open-source
              research, using constants (kgCO2 per km) specific to each mode of
              transportation. This helps us estimate your carbon footprint for
              the distance traveled.
            </div>
          </div>
        </div>
      </div>

      <div className="sticky-button">
        <button
          className="btn btn-primary btn-confirm w-100"
          disabled={isButtonDisabled}
          onClick={handleCalculateClick}
        >
          Count My Emission
        </button>
      </div>
    </div>
  );
};

export default HomePage;
